import React from 'react';
import { Box, Avatar, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import './DisputesTable.css';
import ReportsTableSkeleton from '../../../../components/SkeletonEffect/ReportsTableSkeleton/ReportsTableSkeleton';

function formatText(input) {
  if (!input) return '';
  return input.replace(/_/g, ' ').replace(/^\w/, char => char.toUpperCase());
}

const UserRoleColor = {
  Student: { color: '#6B60E7', bgcolor: '#F0EFFF', value: 'Learner' },
  Teacher: { color: '#BC8D00', bgcolor: '#FFF3CE', value: 'Instructor' },
};

const SessionTypeColor = {
  one_on_one: { color: '#6B60E7', bgcolor: '#F0EFFF', value: 'One-On-One' },
  group: { color: '#14B334', bgcolor: '#F2FFF5', value: 'Group Sessions' },
  prime: { color: '#E0AD14', bgcolor: '#FFFBF0', value: 'Prime Sessions' },
};

const StatusColor = {
  pending: { color: '#E0AD14', bgcolor: '#FFFCF2', value: 'Pending' },
  rejected: { color: '#E14949', bgcolor: '#FFF2F2', value: 'Rejected' },
  resolved: { color: '#14B334', bgcolor: '#F2FAF3', value: 'Resolved' },
  default: { color: '#949DA6', bgcolor: '#F8F9FA', value: 'Unknown' },
};

// Format report ID to display format (full UUID)
function formatReportId(id) {
  if (!id) return '#N/A';
  return `#${id.toUpperCase()}`;
}

export function ReportsTable({ data, loading, startIndex = 0 }) {
  const navigate = useNavigate();

  const handleRowClick = id => {
    navigate(`/admin/payments/reports-details/${id}`, {
      state: { fromReportTab: true },
    });
  };

  if (loading) {
    return <ReportsTableSkeleton />;
  }

  return (
    <Box className="disputes-table-container">
      {data.length === 0 ? (
        <Box className="disputes-empty-state">
          <Typography className="disputes-empty-text">No reports found.</Typography>
        </Box>
      ) : (
        <table className="disputes-table">
          <thead>
            <tr>
              <th className="disputes-th">Report ID</th>
              <th className="disputes-th">User Name</th>
              <th className="disputes-th">User Role</th>
              <th className="disputes-th">Session Type</th>
              <th className="disputes-th">Created on</th>
              <th className="disputes-th">Reason</th>
              <th className="disputes-th">Status</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row, index) => (
              <tr key={row.id} className="disputes-row" onClick={() => handleRowClick(row.id)}>
                <td className="disputes-td">{formatReportId(row.id)}</td>
                <td className="disputes-td">
                  <Box className="disputes-name-cell">
                    <Avatar
                      src={
                        row.userImage && row.userImage.startsWith('http')
                          ? row.userImage
                          : undefined
                      }
                      sx={{ width: 25, height: 25 }}
                      onError={e => {
                        e.target.style.display = 'none';
                      }}
                    >
                      {row.first_name?.charAt(0)?.toUpperCase() ||
                        row.last_name?.charAt(0)?.toUpperCase() ||
                        'U'}
                    </Avatar>
                    <span>
                      {row.first_name} {row.last_name}
                    </span>
                  </Box>
                </td>
                <td className="disputes-td">
                  <Box
                    sx={{
                      height: '30px',
                      paddingLeft: '10px',
                      paddingRight: '10px',
                      paddingTop: '4px',
                      paddingBottom: '4px',
                      borderRadius: 20,
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      display: 'inline-flex',
                      backgroundColor: UserRoleColor[row.userRole]?.bgcolor || '#F8F9FA',
                      color: UserRoleColor[row.userRole]?.color || '#949DA6',
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: 'Poppins',
                        fontSize: '12px',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        lineHeight: 'normal',
                      }}
                    >
                      {UserRoleColor[row.userRole]?.value || '---'}
                    </Typography>
                  </Box>
                </td>
                <td className="disputes-td">
                  <Box
                    sx={{
                      height: '30px',
                      paddingLeft: '10px',
                      paddingRight: '10px',
                      paddingTop: '4px',
                      paddingBottom: '4px',
                      borderRadius: 20,
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      display: 'inline-flex',
                      backgroundColor:
                        SessionTypeColor[row.session_type]?.bgcolor ||
                        SessionTypeColor.one_on_one.bgcolor,
                      color:
                        SessionTypeColor[row.session_type]?.color ||
                        SessionTypeColor.one_on_one.color,
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: 'Poppins',
                        fontSize: '12px',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        lineHeight: 'normal',
                      }}
                    >
                      {SessionTypeColor[row.session_type]?.value ||
                        SessionTypeColor.one_on_one.value}
                    </Typography>
                  </Box>
                </td>
                <td className="disputes-td">{dayjs(row.createdAt).format('DD-MM-YYYY')}</td>
                <td className="disputes-td">{formatText(row.reason)}</td>
                <td className="disputes-td">
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      borderRadius: '20px',
                      border: `1px solid ${StatusColor[row.status.toLowerCase()]?.color || StatusColor.default.color}`,
                      backgroundColor:
                        StatusColor[row.status.toLowerCase()]?.bgcolor ||
                        StatusColor.default.bgcolor,
                      color:
                        StatusColor[row.status.toLowerCase()]?.color || StatusColor.default.color,
                      padding: '4px 12px',
                      maxWidth: 'fit-content',
                    }}
                  >
                    <Box
                      sx={{
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        backgroundColor:
                          StatusColor[row.status.toLowerCase()]?.color || StatusColor.default.color,
                      }}
                    />
                    <Typography
                      sx={{
                        fontFamily: 'Poppins',
                        fontSize: '12px',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        lineHeight: 'normal',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {StatusColor[row.status.toLowerCase()]?.value ||
                        formatText(row.status) ||
                        StatusColor.default.value}
                    </Typography>
                  </Box>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Box>
  );
}
